export const TOOLBAR_COLLAPSE_GAP_VAR = '--toolbar-collapse-gap'

export type ViewportSample = {
    width: number
    visibleHeight: number
    dynamicViewportHeight: number
}

/*
    아이폰 크롬 · 엣지 · 인앱 브라우저는 바가 접혀도 100dvh 가 그대로다.
    폭마다 처음 잰 (보이는 높이 - 100dvh) 를 기준으로, 그보다 커진 만큼만 돌려준다.
 */
export function createToolbarCollapseGapTracker() {
    const baselines = new Map<number, number>()

    return (sample: ViewportSample): number => {
        const gap = sample.visibleHeight - sample.dynamicViewportHeight
        const baseline = baselines.get(sample.width)
        if (baseline === undefined) {
            baselines.set(sample.width, Math.max(0, gap))
            return 0
        }
        const extra = Math.round(gap - baseline)
        return extra > 1 ? extra : 0
    }
}

export function installToolbarCollapseGap(win: Window = window): () => void {
    const doc = win.document
    const probe = doc.createElement('div')
    probe.setAttribute('aria-hidden', 'true')
    probe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:100vh;height:100dvh;visibility:hidden;pointer-events:none'
    doc.body.appendChild(probe)

    const track = createToolbarCollapseGapTracker()
    let applied: number | undefined
    let frame = 0

    const measure = () => {
        frame = 0
        const viewport = win.visualViewport
        const extra = track({
            width: win.innerWidth,
            visibleHeight: Math.max(win.innerHeight, viewport ? viewport.height : 0),
            dynamicViewportHeight: probe.getBoundingClientRect().height
        })
        if (extra === applied) return
        applied = extra
        doc.documentElement.style.setProperty(TOOLBAR_COLLAPSE_GAP_VAR, `${extra}px`)
    }
    const schedule = () => {
        if (!frame) frame = win.requestAnimationFrame(measure)
    }

    measure()
    win.addEventListener('resize', schedule)
    win.addEventListener('orientationchange', schedule)
    win.addEventListener('pageshow', schedule)
    win.visualViewport?.addEventListener('resize', schedule)

    return () => {
        if (frame) win.cancelAnimationFrame(frame)
        win.removeEventListener('resize', schedule)
        win.removeEventListener('orientationchange', schedule)
        win.removeEventListener('pageshow', schedule)
        win.visualViewport?.removeEventListener('resize', schedule)
        probe.remove()
        doc.documentElement.style.removeProperty(TOOLBAR_COLLAPSE_GAP_VAR)
    }
}
